/**
 * 统一的 Query Keys 定义
 * 所有缓存 key 在此集中管理，避免不一致
 */
export const queryKeys = {
    // ============ 中心资产库（Asset Hub）============
    globalAssets: {
        all: () => ['global-assets'] as const,
        characters: (folderId?: string | null) =>
            folderId ? ['global-assets', 'characters', folderId] as const : ['global-assets', 'characters'] as const,
        locations: (folderId?: string | null) =>
            folderId ? ['global-assets', 'locations', folderId] as const : ['global-assets', 'locations'] as const,
        voices: (folderId?: string | null) =>
            folderId ? ['global-assets', 'voices', folderId] as const : ['global-assets', 'voices'] as const,
        folders: () => ['global-assets', 'folders'] as const,
    },

    // ============ 项目资产 ============
    projectAssets: {
        all: (projectId: string) => ['project-assets', projectId] as const,
        characters: (projectId: string) => ['project-assets', projectId, 'characters'] as const,
        locations: (projectId: string) => ['project-assets', projectId, 'locations'] as const,
        detail: (projectId: string) => ['project-assets', projectId, 'detail'] as const,
    },

    // ============ 分镜（Storyboard）============
    storyboards: {
        all: (episodeId: string) => ['storyboards', episodeId] as const,
        panels: (episodeId: string) => ['storyboards', episodeId, 'panels'] as const,
        groups: (episodeId: string) => ['storyboards', episodeId, 'groups'] as const,
    },

    // ============ 视频生成 ============
    videos: {
        all: (episodeId: string) => ['videos', episodeId] as const,
        panels: (episodeId: string) => ['videos', episodeId, 'panels'] as const,
    },

    // ============ 语音（Voice）============
    voiceLines: {
        all: (episodeId: string) => ['voice-lines', episodeId] as const,
        list: (episodeId: string) => ['voice-lines', episodeId, 'list'] as const,
        matched: (projectId: string, episodeId: string) =>
            ['voice-lines', projectId, episodeId, 'matched'] as const,
    },

    // ============ 用户模型 ============
    userModels: {
        all: () => ['user-models'] as const,
    },

    // ============ 任务轮询 ============
    tasks: {
        all: (projectId: string) => ['tasks', projectId] as const,
        target: (projectId: string, targetType: string, targetId: string) =>
            ['tasks', projectId, targetType, targetId] as const,
        targetBatch: (projectId: string, targetType: string, targetIdsKey: string, statusKey: string, typeKey: string, limit: number | '') =>
            ['tasks', projectId, targetType, 'batch', targetIdsKey, statusKey, typeKey, limit] as const,
        snapshot: (projectId: string, targetType: string, targetId: string, typeKey: string) =>
            ['tasks', projectId, targetType, targetId, 'snapshot', typeKey] as const,
        targetStatesAll: (projectId: string) =>
            ['task-target-states', projectId] as const,
        targetStates: (projectId: string, serializedTargets: string) =>
            ['task-target-states', projectId, serializedTargets] as const,
        targetStateOverlay: (projectId: string) =>
            ['task-target-states-overlay', projectId] as const,
        pending: (projectId: string, episodeId?: string) =>
            episodeId
                ? ['pending-tasks', projectId, episodeId] as const
                : ['pending-tasks', projectId] as const,
    },

    // ============ 项目数据 ============
    project: {
        detail: (projectId: string) => ['project', projectId] as const,
        episodes: (projectId: string) => ['project', projectId, 'episodes'] as const,
        data: (projectId: string) => ['project', projectId, 'data'] as const,
    },

    // ============ AI图文 ============
    imageText: {
        projects: (page: number, pageSize: number, search: string) =>
            ['image-text', 'projects', page, pageSize, search] as const,
        project: (projectId: string) => ['image-text', 'project', projectId] as const,
        settings: () => ['image-text', 'settings'] as const,
    },

    // ============ AI短视频 ============
    quickVideo: {
        settings: () => ['quick-video', 'settings'] as const,
    },

    // ============ AI PPT ============
    litePpt: {
        project: (projectId: string) => ['lite-ppt', 'project', projectId] as const,
        settings: () => ['lite-ppt', 'settings'] as const,
    },

    // ============ AI对话作品 ============
    aiChatWorkspaceProjects: {
        all: () => ['ai-chat-workspace-projects'] as const,
        count: (search: string) => ['ai-chat-workspace-projects', 'count', search] as const,
        projects: (page: number, pageSize: number, search: string, includePreview = true) =>
            ['ai-chat-workspace-projects', page, pageSize, search, includePreview ? 'preview' : 'text'] as const,
    },

    // ============ 顶层便捷函数 ============
    workspaceProjects: {
        all: () => ['workspace-projects'] as const,
        list: (page: number, pageSize: number, search: string, projectKind: string) =>
            ['workspace-projects', 'list', page, pageSize, search, projectKind] as const,
    },

    /**
     * 项目基础数据
     */
    projectData: (projectId: string) => ['project-data', projectId] as const,

    /**
     * 剧集详情数据
     */
    episodeData: (projectId: string, episodeId: string) =>
        ['episode-data', projectId, episodeId] as const,
} as const

/**
 * 类型导出，用于类型推断
 */
export type QueryKeys = typeof queryKeys
